import React, { useState, useEffect } from 'react';
import { DataProviderInstance } from '../../../api/DataProvider';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ColorPalette from '../../../colorpalette/ColorPalette';

const RecipientSelector = ({ onRecipientsSelected, loading, setLoading, selectedRecipients }) => {
  const dataProvider = DataProviderInstance();
  const [selectorTab, setSelectorTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [selectedPatients, setSelectedPatients] = useState(selectedRecipients || []);
  const [predefinedQueries, setPredefinedQueries] = useState([]);
  const [selectedQuery, setSelectedQuery] = useState('');
  const [queryParameters, setQueryParameters] = useState({});
  const [queryResults, setQueryResults] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [filterHistory, setFilterHistory] = useState(true);
  const [historyDays, setHistoryDays] = useState(30);

  // Load patients and predefined queries
  useEffect(() => {
    loadPatients();
    loadPredefinedQueries();
  }, []);

  // Update selectedPatients when selectedRecipients prop changes
  useEffect(() => {
    if (selectedRecipients) {
      setSelectedPatients(selectedRecipients);
    }
  }, [selectedRecipients]);

  // Filter patients when search term changes
  useEffect(() => {
    if (patients.length > 0 && searchTerm) {
      const filtered = patients.filter(patient =>
        patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (patient.phone && patient.phone.includes(searchTerm)) ||
        (patient.email && patient.email.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredPatients(filtered);
    } else {
      setFilteredPatients(patients);
    }
  }, [searchTerm, patients]);

  // Load patients from API
  const loadPatients = () => {
    setLoading(true);
    dataProvider.GetPatients(false, (result) => {
      setLoading(false);
      if (result) {
        // LGPD: paciente excluído (lápide) não é destinatário
        const formattedPatients = result.filter(patient => !patient.deleted_at).map(patient => ({
          id: patient.id,
          name: patient.name,
          phone: patient.phone,
          countryCode: patient.countryCode,
          email: patient.email,
          type: 'patient'
        }));
        setPatients(formattedPatients);
        setFilteredPatients(formattedPatients);
        
        // Keep existing selections if any
        if (selectedPatients.length > 0) {
          // Update parent component with selected patients from the new patient list
          const selectedPatientObjects = formattedPatients.filter(patient =>
            selectedPatients.includes(patient.id)
          );
          onRecipientsSelected(selectedPatientObjects);
        } else if (!selectedRecipients || selectedRecipients.length == 0) {
          // Only reset if there are no selected recipients from parent
          setSelectedPatients([]);
          onRecipientsSelected([]);
        }
      }
    });
  };

  // Load predefined queries from API
  const loadPredefinedQueries = () => {
    dataProvider.GetPredefinedQueries((result) => {
      
      if (result && result.success && result.queries) {
        
        setPredefinedQueries(result.queries);
        
        // Set default query if available
        if (result.queries.length > 0) {
          // Check if we have the canceled_last_appointment query and set it as default
          const canceledLastAppointmentQuery = result.queries.find(q => q.id == 'canceled_last_appointment');
          const defaultQuery = canceledLastAppointmentQuery || result.queries[0];
          
          
          setSelectedQuery(defaultQuery.id);
          
          // Initialize parameters with default values
          const defaultParams = {};
          if (defaultQuery.parameters) {
            defaultQuery.parameters.forEach(param => {
              defaultParams[param.name] = param.default;
            });
          }
          
          setQueryParameters(defaultParams);
        }
      } else {
        
      }
    });
  };

  // Handle tab change
  const handleSelectorTabChange = (event, newValue) => {
    setSelectorTab(newValue);
    
    // Update recipients based on the active tab
    if (newValue == 0) {
      // For patient list tab, use the selected patients from all patients, not just filtered
      const selectedPatientObjects = patients.filter(patient =>
        selectedPatients.includes(patient.id)
      );
      onRecipientsSelected(selectedPatientObjects);
    } else if (newValue == 1 && queryResults.length > 0) {
      // Pass the query type when switching to the predefined queries tab
      onRecipientsSelected(queryResults, selectedQuery);
    } else {
      onRecipientsSelected([]);
    }
  };

  // Handle search term change
  const handleSearchTermChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handle query selection change
  const handleQueryChange = (event) => {
    const queryId = event.target.value;
    setSelectedQuery(queryId);
    
    // Reset query parameters
    const query = predefinedQueries.find(q => q.id == queryId);
    if (query && query.parameters) {
      const defaultParams = {};
      query.parameters.forEach(param => {
        defaultParams[param.name] = param.default;
      });
      setQueryParameters(defaultParams);
    } else {
      setQueryParameters({});
    }
    
    // Clear query results
    setQueryResults([]);
  };

  // Handle query parameter change
  const handleParameterChange = (paramName, value) => {
    setQueryParameters(prev => ({
      ...prev,
      [paramName]: value
    }));
  };

  // Execute query
  const executeQuery = () => {
    if (!selectedQuery) return;
    
    setLoading(true);
    dataProvider.GetMassMessagingRecipients(
      selectedQuery,
      queryParameters,
      filterHistory,
      historyDays,
      (result) => {
        setLoading(false);
        if (result && result.success) {
          setQueryResults(result.recipients);
          // Pass the query type along with the recipients
          onRecipientsSelected(result.recipients, selectedQuery);
          
          // Show filtering information if available
          if (result.filter_applied && result.filtered_out > 0) {
            
          }
        }
      }
    );
  };
  
  // Handle patient selection
  const handlePatientSelection = (patientId) => {
    setSelectedPatients(prev => {
      const isSelected = prev.includes(patientId);
      let newSelected;
      
      if (isSelected) {
        // Remove from selection
        newSelected = prev.filter(id => id != patientId);
      } else {
        // Add to selection
        newSelected = [...prev, patientId];
      }
      
      // Update parent component with selected patients from all patients, not just filtered
      const selectedPatientObjects = patients.filter(patient =>
        newSelected.includes(patient.id)
      );
      onRecipientsSelected(selectedPatientObjects);
      
      return newSelected;
    });
  };
  
  // Handle select all patients
  const handleSelectAllPatients = (event) => {
    if (event.target.checked) {
      // Select all patients on the current page
      const pagePatients = filteredPatients
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map(patient => patient.id);
      
      const newSelected = Array.from(new Set([...selectedPatients, ...pagePatients]));
      setSelectedPatients(newSelected);
      
      // Update parent component with selected patients from all patients, not just filtered
      const selectedPatientObjects = patients.filter(patient =>
        newSelected.includes(patient.id)
      );
      onRecipientsSelected(selectedPatientObjects);
    } else {
      // Deselect all patients on the current page
      const pagePatients = filteredPatients
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map(patient => patient.id);
      
      const newSelected = selectedPatients.filter(id => !pagePatients.includes(id));
      setSelectedPatients(newSelected);
      
      // Update parent component with selected patients from all patients, not just filtered
      const selectedPatientObjects = patients.filter(patient =>
        newSelected.includes(patient.id)
      );
      onRecipientsSelected(selectedPatientObjects);
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get current query parameters
  const getCurrentQueryParameters = () => {
    if (!selectedQuery) return [];
    
    const query = predefinedQueries.find(q => q.id == selectedQuery);
    return query ? query.parameters || [] : [];
  };

  // Render patient list tab
  const renderPatientListTab = () => (
    <Box>
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Buscar por nome, telefone ou email"
        value={searchTerm}
        onChange={handleSearchTermChange}
        sx={{ marginBottom: '20px' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      
      <Typography variant="body2" sx={{ marginBottom: '10px' }}>
        {filteredPatients.length} pacientes encontrados
      </Typography>
      
      <TableContainer component={Paper} sx={{
        height: 500,
        overflow: 'auto',
        '& .MuiTableRow-root:nth-of-type(odd)': {
          backgroundColor: ColorPalette.blueLight4
        }
      }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ backgroundColor: ColorPalette.bgLight }}>
                <Checkbox
                  indeterminate={
                    selectedPatients.length > 0 &&
                    selectedPatients.length < filteredPatients
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .length
                  }
                  checked={
                    filteredPatients
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .length > 0 &&
                    filteredPatients
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .every(patient => selectedPatients.includes(patient.id))
                  }
                  onChange={handleSelectAllPatients}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: ColorPalette.bgLight }}>Nome</TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: ColorPalette.bgLight }}>Telefone</TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: ColorPalette.bgLight }}>Email</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPatients
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((patient) => (
                <TableRow
                  key={patient.id}
                  hover
                  onClick={() => handlePatientSelection(patient.id)}
                  role="checkbox"
                  aria-checked={selectedPatients.includes(patient.id)}
                  selected={selectedPatients.includes(patient.id)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedPatients.includes(patient.id)}
                      onChange={() => handlePatientSelection(patient.id)}
                      onClick={(event) => event.stopPropagation()}
                    />
                  </TableCell>
                  <TableCell>{patient.name}</TableCell>
                  <TableCell>{patient.phone}</TableCell>
                  <TableCell>{patient.email || '-'}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredPatients.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Itens por página:"
      />
    </Box>
  );

  // Render predefined queries tab
  const renderPredefinedQueriesTab = () => (
    <Box>
      <FormControl fullWidth sx={{ marginBottom: '20px' }}>
        <InputLabel>Selecione uma consulta predefinida</InputLabel>
        <Select
          value={selectedQuery}
          onChange={handleQueryChange}
          label="Selecione uma consulta predefinida"
        >
          
          {predefinedQueries && predefinedQueries.length > 0 ? (
            predefinedQueries.map((query) => (
              <MenuItem key={query.id} value={query.id}>
                {query.name}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>Nenhuma consulta predefinida disponível</MenuItem>
          )}
        </Select>
      </FormControl>
      
      {selectedQuery && (
        <>
          <Typography variant="body2" sx={{ marginBottom: '10px' }}>
            {predefinedQueries.find(q => q.id == selectedQuery)?.description}
          </Typography>
          
          {getCurrentQueryParameters().length > 0 && (
            <Box sx={{ marginBottom: '20px' }}>
              <Typography variant="subtitle1" sx={{ marginBottom: '10px' }}>
                Parâmetros
              </Typography>
              
              <Grid container spacing={2}>
                {getCurrentQueryParameters().map((param) => (
                  <Grid item xs={12} sm={6} key={param.name}>
                    <TextField
                      fullWidth
                      label={param.label}
                      type={param.type}
                      value={queryParameters[param.name] || ''}
                      onChange={(e) => handleParameterChange(param.name, e.target.value)}
                      helperText={param.description}
                      InputLabelProps={
                        param.type == 'date' ? { shrink: true } : undefined
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          
          {/* Message History Filtering Options */}
          <Box sx={{ marginBottom: '20px' }}>
            <Typography variant="subtitle1" sx={{ marginBottom: '10px' }}>
              Filtro de Histórico de Mensagens
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Filtrar destinatários</InputLabel>
                  <Select
                    value={filterHistory}
                    onChange={(e) => setFilterHistory(e.target.value)}
                    label="Filtrar destinatários"
                  >
                    <MenuItem value={true}>Excluir destinatários que já receberam mensagens</MenuItem>
                    <MenuItem value={false}>Incluir todos os destinatários</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Período de filtro (dias)"
                  type="number"
                  value={historyDays}
                  onChange={(e) => setHistoryDays(parseInt(e.target.value) || 30)}
                  helperText="Excluir destinatários que receberam mensagens neste período"
                  disabled={!filterHistory}
                  InputProps={{ inputProps: { min: 1 } }}
                />
              </Grid>
            </Grid>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
            <button
              onClick={executeQuery}
              disabled={loading}
              style={{
                backgroundColor: ColorPalette.blueMain,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                cursor: 'pointer',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ color: 'white', marginRight: '10px' }} />
                  Executando...
                </>
              ) : (
                'Executar Consulta'
              )}
            </button>
          </Box>
          
          {queryResults.length > 0 && (
            <>
              <Typography variant="body2" sx={{ marginBottom: '10px' }}>
                {queryResults.length} destinatários encontrados
              </Typography>
              
              <TableContainer component={Paper} sx={{
                height: 500,
                overflow: 'auto',
                '& .MuiTableRow-root:nth-of-type(odd)': {
                  backgroundColor: ColorPalette.blueLight4
                }
              }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: ColorPalette.bgLight }}>Nome</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: ColorPalette.bgLight }}>Telefone</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: ColorPalette.bgLight }}>Email</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {queryResults
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((recipient) => (
                        <TableRow key={recipient.id}>
                          <TableCell>{recipient.name}</TableCell>
                          <TableCell>{recipient.phone}</TableCell>
                          <TableCell>{recipient.email || '-'}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={queryResults.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Itens por página:"
              />
            </>
          )}
        </>
      )}
    </Box>
  );

  return (
    <Box>
      <Tabs
        value={selectorTab}
        onChange={handleSelectorTabChange}
        sx={{
          marginBottom: '20px',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            color: ColorPalette.textMedium,
            '&.Mui-selected': {
              color: ColorPalette.blueMain,
              fontWeight: 600
            }
          },
          '& .MuiTabs-indicator': {
            backgroundColor: ColorPalette.blueMain
          }
        }}
      >
        <Tab label="Lista de Pacientes" />
        <Tab label="Consultas Predefinidas" />
      </Tabs>
      
      {loading && selectorTab == 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', marginY: '20px' }}>
          <CircularProgress size={24} sx={{ color: ColorPalette.blueMain }} />
          <Typography variant="body1" sx={{ marginLeft: '10px' }}>
            Carregando pacientes...
          </Typography>
        </Box>
      )}
      
      {selectorTab == 0 && !loading && renderPatientListTab()}
      {selectorTab == 1 && renderPredefinedQueriesTab()}
    </Box>
  );
};

export default RecipientSelector;